/**
 * AdminContext - Shared state for Admin Dashboard
 * Provides centralized state management for all admin tabs
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  // Core state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data state
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [clients, setClients] = useState([]);
  
  // UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [analyticsDateRange, setAnalyticsDateRange] = useState('7days');
  
  // Loading states
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Get timezone offset for analytics
  const getTimezoneOffset = () => -new Date().getTimezoneOffset() / 60;

  // Fetch analytics
  const fetchAnalytics = useCallback(async (dateRange = analyticsDateRange) => {
    setLoadingAnalytics(true);
    try {
      const tzOffset = getTimezoneOffset();
      const res = await axios.get(
        `${API_BASE}/api/admin/analytics?date_range=${dateRange}&tz_offset=${tzOffset}`,
        { withCredentials: true }
      );
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [analyticsDateRange]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/users`, { withCredentials: true });
      setUsers(res.data.users || res.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/notifications`, { withCredentials: true });
      setNotifications(res.data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  // Fetch email stats
  const fetchEmailStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/email/stats`, { withCredentials: true });
      setEmailStats(res.data);
    } catch (err) {
      console.error('Failed to fetch email stats:', err);
    }
  }, []);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/clients`, { withCredentials: true });
      setClients(res.data || []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  }, []);

  // Admin login
  const login = async (password) => {
    try {
      const res = await axios.post(
        `${API_BASE}/api/admin/login`,
        { password },
        { withCredentials: true }
      );
      if (res.data.success) {
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Login failed:', err);
      return false;
    }
  };

  // Admin logout
  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/api/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
    setIsAuthenticated(false);
  };

  // Verify session on mount
  useEffect(() => {
    const verifySession = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/verify`, { withCredentials: true });
        setIsAuthenticated(res.data.authenticated);
      } catch {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    verifySession();
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAnalytics();
      fetchUsers();
      fetchNotifications();
    }
  }, [isAuthenticated, fetchAnalytics, fetchUsers, fetchNotifications]);

  // Update user
  const updateUser = async (userId, data) => {
    try {
      await axios.put(`${API_BASE}/api/admin/users/${userId}`, data, { withCredentials: true });
      toast.success('User updated successfully');
      fetchUsers();
      return true;
    } catch (err) {
      toast.error('Failed to update user');
      return false;
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API_BASE}/api/admin/notifications/${notificationId}`, { withCredentials: true });
      toast.success('Notification deleted');
      fetchNotifications();
      return true;
    } catch (err) {
      toast.error('Failed to delete notification');
      return false;
    }
  };

  // Create notification
  const createNotification = async (notification) => {
    try {
      await axios.post(`${API_BASE}/api/admin/notifications`, notification, { withCredentials: true });
      toast.success('Notification created');
      fetchNotifications();
      return true;
    } catch (err) {
      toast.error('Failed to create notification');
      return false;
    }
  };

  const value = {
    // Auth
    isAuthenticated,
    loading,
    login,
    logout,
    
    // Data
    users,
    analytics,
    notifications,
    emailStats,
    clients,
    
    // UI state
    activeTab,
    setActiveTab,
    analyticsDateRange,
    setAnalyticsDateRange,
    
    // Loading states
    loadingAnalytics,
    loadingUsers,
    loadingNotifications,
    
    // Actions
    fetchAnalytics,
    fetchUsers,
    fetchNotifications,
    fetchEmailStats,
    fetchClients,
    updateUser,
    deleteNotification,
    createNotification,
    
    // Helpers
    getTimezoneOffset,
    API_BASE
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
